import { Fragment, useRef, useState } from 'react';
import type { CSSProperties, FocusEvent, KeyboardEvent } from 'react';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { DatePicker, theme } from 'antd';
import { EmptyState } from '../../../components/EmptyState';
import { DashboardCard } from '../../../components/DashboardCard';
import { formatCurrency } from '../../../lib/format';
import { DATE_LOCALE, capitalize } from '../constants';
import { isFutureDay, parseMoney, planVsRealCellState } from '../lib/plan-vs-real';
import { usePlanVsRealYear } from '../api/usePlanVsRealYear';
import { useT } from '../../../locales';
import './PlanVsRealCard.css';

const YEAR_PICKER_ID = 'dashboard-plan-vs-real-year';

/** Day ticks above the heatmap columns: 1, 5, 10, 15, 20, 25 and 31. */
const DAY_TICKS = [1, 5, 10, 15, 20, 25, 31];

/** Diverging legend: red steps below plan, green steps at/above plan. */
const BELOW_PLAN_STEPS = ['r4', 'r3', 'r2', 'r1'] as const;
const ABOVE_PLAN_STEPS = ['g1', 'g2', 'g3'] as const;

/** Short localized month label (Ene..Dic) for a 1..12 month index. */
function monthLabel(month: number): string {
  return capitalize(
    new Intl.DateTimeFormat(DATE_LOCALE, { month: 'short' }).format(new Date(2000, month - 1, 1)),
  );
}

/**
 * Yearly plan-vs-real heatmap (GitHub-contributions style, 12 months x up to
 * 31 days). The card owns its year state; the plan side is a static reference
 * from the current active clients, and the real side reflects the invoices of
 * the selected year, so changing the year only refetches this card.
 *
 * `now` is a test seam for the future-day logic: it only decides which day
 * cells are future (non-interactive gray placeholders); production renders
 * it without the prop, defaulting to the real clock.
 */
export function PlanVsRealCard({ now = dayjs() }: { now?: Dayjs }) {
  const t = useT();
  const { token } = theme.useToken();
  const [year, setYear] = useState<string>(() => String(dayjs().year()));
  const [selected, setSelected] = useState<{ month: number; dia: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  /** True while the pointer is down on a cell that already has focus, so the
      following click is a re-tap that clears the selection instead of moving it. */
  const retapRef = useRef(false);

  const { data, isLoading } = usePlanVsRealYear(year);

  const months = data?.months ?? [];
  const hasData = months.some((m) =>
    m.days.some((d) => parseMoney(d.plan) > 0 || parseMoney(d.real) > 0),
  );

  /** Number of existing days for a 1..12 month index in the loaded data. */
  const dayCount = (month: number): number => months[month - 1]?.days.length ?? 0;

  /**
   * Interactive days of a month row of the selected year: a past year keeps
   * the full day count; in the current year, passed months keep the full
   * count, the current month keeps days up to today (interactive), and future
   * months have zero; a fully-future year has zero everywhere. Used to clamp
   * arrow-key navigation so it never lands on a future placeholder.
   */
  const interactiveDayCount = (month: number): number => {
    const full = dayCount(month);
    const cellYear = Number(year);
    if (cellYear < now.year()) return full;
    if (cellYear > now.year()) return 0;
    if (month < now.month() + 1) return full;
    if (month === now.month() + 1) return Math.min(now.date(), full);
    return 0;
  };

  /**
   * Roving tabindex target: the selected cell when one is selected, otherwise
   * the first day of the first month. Exactly one cell keeps tabIndex=0;
   * Jan 1 is always interactive in past and current years, and a fully-
   * future year renders no buttons at all, so no tab stop leaks onto a
   * placeholder.
   */
  const focusTarget = selected ?? { month: 1, dia: 1 };

  /** Focus is the single source of truth for selection: tap, click, and
      keyboard focus all bubble a focusin event that the container handles. */
  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const { month, dia } = target.dataset;
    if (month === undefined || dia === undefined) return;
    retapRef.current = false;
    setSelected({ month: Number(month), dia: Number(dia) });
  };

  /** Arrow keys move focus between interactive cells (selection follows via
      focus); movement clamps to the interactive day count, so a month with
      no interactive days is a no-op. Escape clears the selection and blurs
      the active cell. */
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const { month: monthAttr, dia: diaAttr } = target.dataset;
    if (monthAttr === undefined || diaAttr === undefined) return;
    const from = { month: Number(monthAttr), dia: Number(diaAttr) };

    let to: { month: number; dia: number } | null = null;
    switch (event.key) {
      case 'ArrowRight':
        to = { ...from, dia: Math.min(from.dia + 1, interactiveDayCount(from.month)) };
        break;
      case 'ArrowLeft':
        to = { ...from, dia: Math.max(from.dia - 1, 1) };
        break;
      case 'ArrowDown': {
        const month = Math.min(from.month + 1, 12);
        const limit = interactiveDayCount(month);
        // No interactive day in the target month: stay put (no-op).
        if (limit > 0) to = { month, dia: Math.min(from.dia, limit) };
        break;
      }
      case 'ArrowUp': {
        const month = Math.max(from.month - 1, 1);
        const limit = interactiveDayCount(month);
        if (limit > 0) to = { month, dia: Math.min(from.dia, limit) };
        break;
      }
      case 'Escape': {
        const active = document.activeElement;
        if (
          active instanceof HTMLElement &&
          active.dataset.month !== undefined &&
          containerRef.current?.contains(active)
        ) {
          active.blur();
        }
        setSelected(null);
        return;
      }
      default:
        return;
    }

    // Down/Up may be a no-op when the target month has no interactive days.
    if (to === null) return;
    event.preventDefault();
    containerRef.current
      ?.querySelector<HTMLElement>(`.plan-vs-real__cell[data-month="${to.month}"][data-dia="${to.dia}"]`)
      ?.focus();
  };

  /** Pointer-down on a cell that is already selected marks a re-tap; the
      click that follows then clears the selection. Keyboard focus never
      re-taps, and a first tap on an unselected cell leaves retapRef false
      so the focus/click above keeps the selection. */
  const handlePointerDown = (cell: { month: number; dia: number }) => {
    retapRef.current = selected?.month === cell.month && selected?.dia === cell.dia;
  };

  const handleClick = (cell: { month: number; dia: number }) => {
    const retap = retapRef.current;
    retapRef.current = false;
    if (retap) {
      setSelected(null);
    } else {
      // Covers inputs where the click does not move focus (synthetic/touch).
      // Real taps already selected via the focus event above, so this is a no-op.
      setSelected(cell);
    }
  };

  /** Theme tokens drive the cell palette; light/dark stay legible via color-mix. */
  const heatmapVars = {
    '--pvr-red': token.colorError,
    '--pvr-green': token.colorSuccess,
    '--pvr-empty': token.colorFillQuaternary,
    '--pvr-neutral': token.colorFillTertiary,
    '--pvr-canvas': token.colorBgContainer,
  } as CSSProperties;

  /** Day data of the selected cell for the detail strip (or undefined). */
  const selectedDayData = selected
    ? months.find((m) => m.month === selected.month)?.days[selected.dia - 1]
    : undefined;

  /** Native tooltip: "Mes Día — Plan: QX · Real: QY · Cumplimiento: N%".
      One decimal when non-integer (99.8%) so the text matches the color
      bucket, which is computed from the unrounded ratio. */
  const cellTooltip = (monthIndex: number, dia: number, plan: string, real: string): string => {
    const planNum = parseMoney(plan);
    const realNum = parseMoney(real);
    const pct = planNum > 0 ? Number(((realNum / planNum) * 100).toFixed(1)) : null;
    const cumplimiento = pct === null ? '—' : `${pct}%`;
    return `${monthLabel(monthIndex)} · ${t('dashboard.planVsRealDia', { dia })} — ${t(
      'dashboard.planVsRealPlan',
    )}: ${formatCurrency(plan)} · ${t('dashboard.planVsRealReal')}: ${formatCurrency(
      real,
    )} · ${t('dashboard.planVsRealCumplimiento')}: ${cumplimiento}`;
  };

  return (
    <DashboardCard
      className="plan-vs-real-card"
      headerClassName="plan-vs-real__header"
      loading={isLoading && !data}
      style={{ height: '100%' }}
      bodyStyle={{ padding: '18px 22px 24px' }}
      title={t('dashboard.planVsRealTitle')}
      subtitle={t('dashboard.planVsRealSubtitle')}
      extra={
        <div className="plan-vs-real__year-control">
          <label htmlFor={YEAR_PICKER_ID} style={{ color: token.colorTextSecondary }}>
            {t('dashboard.planVsRealYear')}
          </label>
          <DatePicker
            id={YEAR_PICKER_ID}
            aria-label={t('dashboard.planVsRealYear')}
            picker="year"
            value={dayjs(`${year}-01-01`)}
            onChange={(next) => {
              if (next) {
                setYear(next.format('YYYY'));
                // The previous selection may point at a day the new year lacks
                // (e.g. Feb 29 on a common year); start each year clean.
                setSelected(null);
              }
            }}
            allowClear={false}
          />
        </div>
      }
    >
      {!hasData ? (
        <EmptyState
          compact
          title={t('dashboard.planVsRealSinDatos')}
          description={t('dashboard.planVsRealSinDatosDesc')}
        />
      ) : (
        <div
          ref={containerRef}
          className="plan-vs-real__heatmap"
          role="group"
          aria-label={t('dashboard.planVsRealAria')}
          style={heatmapVars}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
        >
          {/* Header row: label spacer, then the day ticks aligned to their columns. */}
          <div aria-hidden="true" />
          {DAY_TICKS.map((day) => (
            <span
              key={day}
              className="plan-vs-real__day-tick"
              style={{ gridColumnStart: day + 1 }}
            >
              {day}
            </span>
          ))}

          {/* 12 month rows: label + exactly 31 day cells, so every row has
              the same grid item count and auto-placement stays aligned.
              Days the month does not have get an invisible placeholder. */}
          {months.map((m) => (
            <Fragment key={m.month}>
              <span className="plan-vs-real__month-label">{monthLabel(m.month)}</span>
              {Array.from({ length: 31 }, (_, dayIndex) => {
                const dia = dayIndex + 1;
                const day = m.days[dia - 1];
                if (!day) {
                  // Day does not exist in the month: invisible placeholder.
                  return (
                    <span
                      key={dia}
                      className="plan-vs-real__cell plan-vs-real__cell--none"
                      aria-hidden="true"
                    />
                  );
                }
                if (isFutureDay(year, m.month, day.dia, now)) {
                  // The day has not happened yet: non-interactive gray
                  // placeholder, never a button with data attributes.
                  return (
                    <span
                      key={dia}
                      className="plan-vs-real__cell plan-vs-real__cell--future"
                      aria-hidden="true"
                    />
                  );
                }
                return (
                  <button
                    key={dia}
                    type="button"
                    className={`plan-vs-real__cell plan-vs-real__cell--${planVsRealCellState(
                      day.plan,
                      day.real,
                    )}`}
                    title={cellTooltip(m.month, day.dia, day.plan, day.real)}
                    data-month={m.month}
                    data-dia={day.dia}
                    aria-pressed={selected !== null && selected.month === m.month && selected.dia === day.dia}
                    tabIndex={focusTarget.month === m.month && focusTarget.dia === day.dia ? 0 : -1}
                    onPointerDown={() => handlePointerDown({ month: m.month, dia: day.dia })}
                    onClick={() => handleClick({ month: m.month, dia: day.dia })}
                  />
                );
              })}
            </Fragment>
          ))}

          {selected && selectedDayData && (
            <div className="plan-vs-real__detail" role="status">
              {cellTooltip(selected.month, selected.dia, selectedDayData.plan, selectedDayData.real)}
            </div>
          )}

          {/* Diverging legend: swatch runs share the cell palette; small
              magnitude anchors under the runs mark the 0%, 100% and 150%+
              points (numeric-only, no locale keys). */}
          <div className="plan-vs-real__legend">
            <span className="plan-vs-real__legend-item">
              <span className="plan-vs-real__swatch-block">
                <span className="plan-vs-real__swatch-row">
                  {BELOW_PLAN_STEPS.map((step) => (
                    <span key={step} className={`plan-vs-real__swatch plan-vs-real__swatch--${step}`} />
                  ))}
                </span>
                <span className="plan-vs-real__legend-anchors">
                  <span className="plan-vs-real__legend-anchor">0%</span>
                  <span className="plan-vs-real__legend-anchor">100%</span>
                </span>
              </span>
              <span className="plan-vs-real__legend-label">
                {t('dashboard.planVsRealLegendBelow')}
              </span>
            </span>
            <span className="plan-vs-real__legend-item">
              <span className="plan-vs-real__swatch-block">
                <span className="plan-vs-real__swatch-row">
                  {ABOVE_PLAN_STEPS.map((step) => (
                    <span key={step} className={`plan-vs-real__swatch plan-vs-real__swatch--${step}`} />
                  ))}
                </span>
                <span className="plan-vs-real__legend-anchors plan-vs-real__legend-anchors--end">
                  <span className="plan-vs-real__legend-anchor">150%+</span>
                </span>
              </span>
              <span className="plan-vs-real__legend-label">
                {t('dashboard.planVsRealLegendAbove')}
              </span>
            </span>
            <span className="plan-vs-real__legend-item">
              <span className="plan-vs-real__swatch plan-vs-real__swatch--no-plan" />
              <span className="plan-vs-real__legend-label">
                {t('dashboard.planVsRealNoPlan')}
              </span>
            </span>
            {isFutureDay(year, 12, 31, now) && (
              <span className="plan-vs-real__legend-item">
                <span className="plan-vs-real__swatch plan-vs-real__swatch--future" />
                <span className="plan-vs-real__legend-label">
                  {t('dashboard.planVsRealLegendFuture')}
                </span>
              </span>
            )}
          </div>
        </div>
      )}
    </DashboardCard>
  );
}
